import { engine } from '@play-co/timestep-core';
import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import Scroll from 'src/game/components/shared/Scroll';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { user } from 'src/StateObserver';
import { animDuration, withLoading } from 'src/lib/utils';
import ButtonScaleViewWithText, {
  Opts,
} from 'src/lib/ui/components/ButtonScaleViewWithText';
import i18n from 'src/lib/i18n/i18n';
import {
  LeagueLeaderboard,
  LeagueLeaderboardEntry,
} from 'src/replicant/match2/leagues/leagues.ruleset';
import { getCurrentLeagueId } from 'src/replicant/match2/leagues/leagues.getters';
import { downscale } from 'src/conf/scale';
import PopupMatch2BaseFullscreen from 'src/game/minigame/match2-timestep/components/PopupMatch2BaseFullscreen';
import { getLeagueLeaderboard } from 'src/game/minigame/match2-timestep/leagues/league.sequences';
import { LeaguesLeadeboardView } from 'src/game/minigame/match2-timestep/leagues/components/LeaguesLeadeboardView';
import { ChampionsLeagueOriginFeature } from 'src/replicant/championsLeague/championsLeague.analytics.schema';
import { track } from 'src/lib/analytics/utils';
import { getPlayerProfile } from 'src/replicant/profile/profile.getters';
import { GENERIC_AVATAR } from 'src/replicant/profile/assets';

const entryScale = 1.25;
const tabWidth = 361;
const tabHeight = 77;
const highlightLineHeight = 4;
const activeTabTextColor = '#BF4747';
const inactiveTabTextColor = '#BF8147';
const highlightLineImage =
  'assets/ui/championsLeague/popups/tab_highlight_line.png';

export default class PopupChampionsLeagueLeaderboard extends PopupMatch2BaseFullscreen {
  private readonly scroll: Scroll<
    { message: string } | (LeagueLeaderboardEntry & { hideReward?: boolean }),
    LeaguesLeadeboardView
  >;
  private readonly header: ImageScaleView;
  private readonly mainPlayer: LeaguesLeadeboardView;
  private readonly scrollArea: ImageScaleView;
  private tickHandler: () => void = () => void 0;
  private playerRank?: number;
  private tabs: ButtonScaleViewWithText[] = [];
  private originFeature!: ChampionsLeagueOriginFeature;
  private loaded = false;

  constructor(opts: { superview: View; close: (success: boolean) => void }) {
    super({
      superview: opts.superview,
      zIndex: 10000,
      close: () => opts.close(false),
      tracking: {
        feature: Feature.MATCH2.LEAGUES._,
        $subFeature: Feature.MATCH2.LEAGUES.LEADERBOARD,
      },
      bgColor: '#fae5b7',
      cta: {
        footerHeight: 170,
        localeText: () => i18n('championsLeague.play'),
        onClick: async () => {
          opts.close(true);
        },
      },
    });

    const { width, height } = this.getView().style;
    const locale = StateObserver.getLocale() === 'en' ? 'en' : 'ja';

    this.header = new ImageScaleView({
      superview: this.panel,
      image: 'assets/ui/championsLeague/popups/header.png',
      width: 720,
      height: 252,
      scaleMethod: 'contain',
    });

    new ImageScaleView({
      superview: this.header,
      scaleMethod: 'contain',
      canHandleEvents: false,
      image: `assets/ui/championsLeague/popups/championsleague_${locale}.png`,
      width: downscale(848),
      height: downscale(133),
      centerOnOrigin: true,
      x: this.header.style.width * 0.5,
      y: 80,
    });

    const scrollHeight =
      height - this.footerHeight - this.header.style.height + 5;

    this.scrollArea = new ImageScaleView({
      superview: this.panel,
      width,
      height: scrollHeight,
      y: this.header.style.height,
      backgroundColor: '#FF0000',
      visible: false,
    });

    new ImageScaleView({
      superview: this.panel,
      image: highlightLineImage,
      width,
      height: highlightLineHeight,
      y: this.header.style.height,
      scaleMethod: 'stretch',
      canHandleEvents: false,
    });

    this.scroll = new Scroll({
      superview: this.panel,
      createItem: this.createItem.bind(this),
      recycle: true,
      setData: this.setItemData.bind(this),
      rect: {
        x: this.scrollArea.style.x + 22,
        y: this.scrollArea.style.y + 5,
        width: this.scrollArea.style.width - 10,
        height: this.scrollArea.style.height - 10,
      },
      margin: 30,
      firstItemOffset: -15,
      lastItemOffset: -15,
    });

    this.mainPlayer = new LeaguesLeadeboardView({
      superview: this.panel,
      width: (width + 6) / entryScale,
      height: 82,
      scale: entryScale,
      x: this.scrollArea.style.x - 3,
      y: this.scrollArea.style.y + this.scrollArea.style.height,
    });

    this.createTabButton({
      superview: this.header,
      x: this.header.style.width / 4,
      y: this.header.style.height - tabHeight * 0.5,
      localeText: () => i18n('frenzy.popupLeaderboard.tabGlobal'),
      onClick: () => this.onSelectGlobalTab(),
    });

    this.createTabButton({
      superview: this.header,
      x: (3 * this.header.style.width) / 4,
      y: this.header.style.height - tabHeight * 0.5,
      localeText: () => i18n('frenzy.popupLeaderboard.tabFriends'),
      onClick: () => this.onSelectFriendsTab(),
    });
  }

  private async onSelectGlobalTab() {
    this.scroll.setItems([]);
    const data = await withLoading(() => getLeagueLeaderboard().get(), {
      feature: Feature.LEAGUES._,
      subFeature: Feature.LEAGUES.GET_LEADERBOARD,
    });
    if (data?.leaderboard.length === 0) {
      this.scroll.setItems([{ message: i18n('match2.team.no_results') }]);
    } else {
      this.scroll.setItems([...data!.leaderboard]);
    }
    this.setupMainPlayer(data!);

    // Track the leaderboard view only once per popup - user can switch tabs
    if (!this.loaded) {
      const leagueId = getCurrentLeagueId(user());
      track('ChampionsLeaderboardView', {
        originFeature: this.originFeature,
        leagueId,
        rank: data?.playerRank ?? 0,
        medals: user().match2.leagues[leagueId]?.score ?? 0,
      });
    }
    this.loaded = true;
  }

  private async onSelectFriendsTab() {
    this.scroll.setItems([]);
    const data = await withLoading(() => getLeagueLeaderboard(true).get(), {
      feature: Feature.LEAGUES._,
      subFeature: Feature.LEAGUES.GET_LEADERBOARD,
    });
    if (data.leaderboard.length === 0) {
      this.scroll.setItems([{ message: i18n('match2.team.no_results') }]);
    } else {
      this.scroll.setItems([
        ...data.leaderboard.map((x) => ({ ...x, hideReward: true })),
      ]);
    }
    this.setupMainPlayer(data!);
  }

  public init(args: { originFeature: ChampionsLeagueOriginFeature }) {
    super.init({});
    this.originFeature = args.originFeature;
  }

  private createItem(
    superview: View,
    i: number,
    data:
      | { message: string }
      | (LeagueLeaderboardEntry & { hideReward?: boolean }),
  ) {
    const entry = new LeaguesLeadeboardView({
      superview,
      scale: entryScale,
      width: 545,
      height: 75,
    });
    entry.setup(data);
    return entry;
  }

  private setItemData(
    i: number,
    data:
      | { message: string }
      | (LeagueLeaderboardEntry & { hideReward?: boolean }),
    view: LeaguesLeadeboardView,
  ) {
    view.setup(data);
  }

  public override async fadeIn(): Promise<void> {
    this.tickHandler = this.tick.bind(this);
    engine.addListener('Tick', this.tickHandler);
    return super.fadeIn();
  }

  public override async fadeOut(): Promise<void> {
    engine.removeListener('Tick', this.tickHandler);
    return super.fadeOut();
  }

  private tick() {
    // Not properly initialized yet, probably the async getter isn't ready
    if (this.playerRank == null) {
      this.mainPlayer.hide();
      return;
    }

    if (this.scroll.isItemVisible(this.playerRank)) {
      this.mainPlayer.hide();
      return;
    }

    const top =
      this.scroll.getElementPosition(this.playerRank) <
      this.scroll.viewportUpperBounds;

    this.mainPlayer.updateOpts({
      visible: true,
      y: top
        ? this.scrollArea.style.y + 4
        : this.scrollArea.style.y +
          this.scrollArea.style.height -
          this.mainPlayer.style.height * entryScale -
          12,
    });
  }

  private createTabButton(opts: Omit<Opts, 'tracking'>) {
    const view = new ButtonScaleViewWithText({
      image: 'assets/ui/match2/leagues/tab_inactive.png',
      width: tabWidth,
      height: tabHeight,
      textColor: inactiveTabTextColor,
      font: 'Body',
      fontSize: 42,
      ...opts,
      tracking: {
        feature: Feature.MATCH2.LEAGUES._,
        $subFeature: Feature.MATCH2.LEAGUES.LEADERBOARD_TAB,
      },
      centerOnOrigin: true,
      zIndex: -1,
    });

    const setActive = (tab: ButtonScaleViewWithText, active: boolean) => {
      tab.updateButtonImages({
        image: `assets/ui/match2/leagues/tab_${
          active ? 'active' : 'inactive'
        }.png`,
      });

      tab.updateOpts({
        zIndex: active ? 0 : -1,
      });
      tab.label.color = active ? activeTabTextColor : inactiveTabTextColor;
    };

    view.onClick = async () => {
      this.tabs.forEach((tab) => {
        const active = tab === view;
        setActive(tab, active);
      });

      await opts.onClick?.();
    };

    this.tabs.push(view);

    if (this.tabs.length === 1) {
      void view.onTap();
    }
  }

  private setupMainPlayer(data: LeagueLeaderboard) {
    const { name, photo } = getPlayerProfile(
      user(),
      user().publicProfile?.name,
      user().publicProfile?.photo,
      user().profile?.name,
      user().profile?.photo,
    );
    const score = user().match2.leagues[getCurrentLeagueId(user())]?.score;
    this.mainPlayer.setup({
      id: user().id,
      rank: data.playerRank,
      score,
      name: name ?? 'Player',
      photo: photo ?? GENERIC_AVATAR,
      me: true,
      hideReward: true,
      modifiedAt: 0,
    });
    this.playerRank = data!.playerRank;
    this.mainPlayer.onInputSelect = () =>
      this.scroll.centerOn(this.playerRank!, animDuration);
  }
}
